import { mapGetters, mapState } from 'vuex'

export default {
  computed: {
    ...mapGetters('presentationReport', [
      'presentationReport',
      'selectedPresentation',
      'isLoadingReport',
      'reportError',
      'connectionError',
      'isShowReportPanel',
      'avgCorrectRate',
      'mostDifficultQuestions',
      'engagementLevel',
      'totalInteractions',
      'privateFeedbacks',
      'newPrivateFeedbacks',
      'audienceReport',
    ]),
    ...mapState({
      presentation: (state) => state.presentation,
      audienceReportSummary: (state) => state.presentationReport.audienceReportSummary,
    }),

    reportMetric() {
      return this.audienceReportSummary
    },
    feedbackListLength() {
      return (this.feedbackList && this.feedbackList.length) || 0
    },
    feedbackList() {
      const hasNewFeedbacks = this.newPrivateFeedbacks
          && (
            Object.prototype.hasOwnProperty.call(this.newPrivateFeedbacks, 'comments')
              || Object.prototype.hasOwnProperty.call(this.newPrivateFeedbacks, 'ratings')
          )
      const hasOldFeedbacks = this.privateFeedbacks && this.privateFeedbacks.length

      if (hasNewFeedbacks && hasOldFeedbacks) {
        return this.mergeFeedbackVersions(this.privateFeedbacks, this.newPrivateFeedbacks)
      }

      if (hasOldFeedbacks) return this.mergeFeedbackVersions(this.privateFeedbacks, {})

      if (hasNewFeedbacks) return this.mergeFeedbackVersions([], this.newPrivateFeedbacks)

      return []
    },
    avgRating() {
      let avg = 0
      if (this.feedbackListLength) {
        let numberOfRatings = 0
        const totalRating = this.feedbackList.reduce((
          total, current,
        ) => {
          const rating = current[0].rating || 0

          if (rating) {
            numberOfRatings++
          }

          return total + rating
        }, 0)

        avg = numberOfRatings
          ? parseFloat(totalRating / numberOfRatings).toFixed(1)
          : parseFloat(totalRating).toFixed(1)
      }

      return avg
    },
    totalRatings() {
      let total = 0

      if (this.feedbackListLength) {
        for (let i = 0; i < this.feedbackListLength; i++) {
          if (this.feedbackList[i][0] && this.feedbackList[i][0].rating) {
            total++
          }
        }
      }

      return total
    },

  },
  methods: {

    _transformAudienceFeedbacksV1(oldFeedbackList) {
      const feedbacks = {}

      oldFeedbackList.forEach((feedback) => {
        if (Object.prototype.hasOwnProperty.call(feedbacks, `${feedback.audience}`)) {
          feedbacks[`${feedback.audience}`].push(feedback)
        } else {
          feedbacks[`${feedback.audience}`] = [feedback]
        }
      })

      let groupFeedbackWithDate = []

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        groupFeedbackWithDate.push({
          feedbacks: feedbacks[property],
          lastUpdatedAt: feedbacks[property][0].updatedAt,
        })
      }

      groupFeedbackWithDate = groupFeedbackWithDate.map((group) => {
        const { feedbacks: feedbackListByAudience, lastUpdatedAt } = group
        const groupFeedbacks = feedbackListByAudience.map((item) => {
          const rating = item.voteCount + 2 // old rating base [0..3]
          const newRating = rating > 5 ? 5 : rating // some old rating data greater than 3

          return {
            id: item.id,
            presentationId: item.presentationId,
            rating: newRating,
            comment: item.title,
            createdBy: null,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            audience: {
              id: null,
              email: item.email,
              audienceId: item.audience,
              emoji: item.audienceEmoji,
              name: item.audienceName,
              color: item.audienceColor,
            },
          }
        })

        return {
          feedbacks: groupFeedbacks,
          lastUpdatedAt,
        }
      })

      return groupFeedbackWithDate
    },

    _transformAudienceFeedbacksV2(newFeedbackList) {
      const {
        comments,
        ratings,
      } = newFeedbackList
      const feedbacks = {}

      comments.forEach((comment) => {
        const { audienceId } = comment.audience

        if (Object.prototype.hasOwnProperty.call(feedbacks, audienceId)) {
          feedbacks[audienceId].push(comment)
        } else {
          feedbacks[audienceId] = [comment]
        }
      })

      ratings.forEach((rating) => {
        const { audienceId } = rating.audience

        if (Object.prototype.hasOwnProperty.call(feedbacks, audienceId)) {
          feedbacks[audienceId] = feedbacks[audienceId].map((item) => ({
            ...item,
            rating: rating.rating,
          }))
        } else {
          feedbacks[audienceId] = [rating]
        }
      })

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        feedbacks[property] = feedbacks[property].reverse()
      }

      const groupFeedbackWithDate = []

      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const property in feedbacks) {
        groupFeedbackWithDate.push({
          feedbacks: feedbacks[property],
          lastUpdatedAt: feedbacks[property][0].updatedAt,
        })
      }

      return groupFeedbackWithDate
    },

    mergeFeedbackVersions(feedbackListV1 = [], feedbackListV2 = {}) {
      const listV1 = this._transformAudienceFeedbacksV1(feedbackListV1)
      const listV2 = this._transformAudienceFeedbacksV2(feedbackListV2)
      let mergedData = [
        ...listV1,
        ...listV2,
      ]

      mergedData = this._sortByDateAsc(mergedData)

      mergedData = this._flatFeedbackArray(mergedData)

      return mergedData
    },

    _flatFeedbackArray(list) {
      return list.map((listItem) => {
        const { feedbacks } = listItem

        return feedbacks.map((item) => ({
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          comment: item.comment,
          rating: item.rating,
          color: item.audience.color,
          emoji: item.audience.emoji,
          name: item.audience.name,
          email: item.audience.email,
          isAnonymous: item.audience.isAnonymous,
        }))
      })
    },

    _sortByDateAsc(arr) {
      const newArr = [...arr]

      newArr.sort((a, b) => {
        return new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime()
      })

      return newArr
    },
    formatNumberForMetric(number) {
      if (!number) {
        return 0
      }
      const roundedNumber = number.toFixed(2)
      return parseFloat(roundedNumber)
    },
    reportMetricField() {
      if (!this.reportMetric) return { presentationId: this.presentation.id }
      return {
        presentationId: this.presentation.id,
        feedbackCount: this.totalRatings,
        participantsCount: this.reportMetric && this.reportMetric.totalAudience,
        averageSubmissions: this.formatNumberForMetric((this.reportMetric.averageSubmissionCount || 0)),
        accuracy: Math.round(this.reportMetric.totalAccuracy * 100 || 0),
      }
    },
  },
}
